import React, { useState } from 'react';
import { Button } from 'react-bootstrap';

import Checkbox from 'components/DesignSystem/Checkbox';
import Icon from 'components/DesignSystem/Icon';
import RadioButton from 'components/DesignSystem/RadioButton';
import {
  Modelo, modeloElegivel, modeloNaoSuportaTipoRodada, TipoRodada,
} from 'helper/windx';
import { parseISO } from 'date-fns';
import './index.scss';

export type ModoSelecaoModelos = 'checkbox' | 'radio';

interface CategoriaModelosProps {
  chave: string;
  titulo: string;
  modelos: Modelo[];
  idsSelecionados: number[];
  idsIndisponiveis?: Set<number>;
  datasRodadaPorId?: Record<number, string>;
  tipoRodada?: TipoRodada;
  modoSelecao?: ModoSelecaoModelos;
  defaultOpen?: boolean;
  onChange: (ids: number[]) => void;
  onSelecionarTodos: (modelosDoGrupo: Modelo[]) => void;
  onLimpar: (modelosDoGrupo: Modelo[]) => void;
}

function formatarDataBR(iso: string): string {
  const d = parseISO(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString('pt-BR');
}

export default function CategoriaModelos({
  chave,
  titulo,
  modelos,
  idsSelecionados,
  idsIndisponiveis,
  datasRodadaPorId,
  tipoRodada,
  modoSelecao = 'checkbox',
  defaultOpen = false,
  onChange,
  onSelecionarTodos,
  onLimpar,
}: CategoriaModelosProps) {
  const [aberto, setAberto] = useState(defaultOpen);
  const indisponiveisSet = idsIndisponiveis ?? new Set();

  const elegivel = (m: Modelo): boolean => modeloElegivel(m, indisponiveisSet, tipoRodada);

  const totalElegiveisGrupo = modelos.filter(elegivel).length;
  const idsSelecionadosNoGrupo = modelos.filter((m) => (
    idsSelecionados.includes(m.id)
  )).length;

  const toggle = (id: number) => {
    if (idsSelecionados.includes(id)) {
      onChange(idsSelecionados.filter((i) => i !== id));
    } else {
      onChange([...idsSelecionados, id]);
    }
  };

  const selecionarRadio = (id: number) => {
    onChange([id]);
  };

  const isRadio = modoSelecao === 'radio';

  const toggleAberto = () => {
    setAberto(!aberto);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      toggleAberto();
    }
  };

  return (
    <div
      className="windx-categoria-modelos"
      data-testid={`windx-modelos-grupo-${chave}`}
    >
      <div
        className="windx-categoria-modelos-header"
        role="button"
        tabIndex={0}
        onClick={toggleAberto}
        onKeyDown={handleKeyDown}
        aria-expanded={aberto}
        aria-controls={`categoria-modelos-body-${chave}`}
        data-testid={`windx-modelos-header-${chave}`}
      >
        <div
          className="windx-categoria-modelos-chevron"
          data-testid={`windx-modelos-chevron-${chave}`}
          data-aberto={aberto ? 'true' : 'false'}
        >
          <Icon
            iconType={aberto ? 'down_arrow' : 'right_arrow'}
            size="small"
            className="windx-categoria-modelos-chevron-icon"
          />
        </div>
        <span className="windx-categoria-modelos-titulo">{titulo}</span>
        <div className="windx-categoria-modelos-contador">
          <span
            className={`windx-categoria-modelos-contador-badge ${
              idsSelecionadosNoGrupo > 0 ? 'ativo' : 'inativo'
            }`}
            data-testid={`windx-modelos-contador-${chave}`}
          >
            {`${idsSelecionadosNoGrupo} / ${totalElegiveisGrupo}`}
          </span>
        </div>
      </div>

      <div
        className="windx-categoria-modelos-body"
        id={`categoria-modelos-body-${chave}`}
        hidden={!aberto}
      >
        {modelos.map((m) => {
          const indisponivel = indisponiveisSet.has(m.id);
          const semSuporteTipo = modeloNaoSuportaTipoRodada(m, tipoRodada);
          const dataRodada = datasRodadaPorId?.[m.id];
          const dataBR = dataRodada ? formatarDataBR(dataRodada) : '';
          let sufixo = '';
          if (indisponivel) sufixo += ' (sem dados)';
          else if (dataBR) sufixo += ` (${dataBR})`;
          if (semSuporteTipo && m.tipo_simulacao && m.tipo_simulacao.length === 1) {
            sufixo += ` (apenas ${m.tipo_simulacao[0]})`;
          }

          if (isRadio) {
            return (
              <RadioButton
                key={m.id}
                id={`windx-modelo-${m.id}`}
                name="windx-modelo-radio"
                label={`${m.label}${sufixo}`}
                checked={idsSelecionados.includes(m.id)}
                disabled={indisponivel || semSuporteTipo}
                onChange={() => selecionarRadio(m.id)}
                data-testid={`windx-modelo-radio-${m.id}`}
              />
            );
          }

          return (
            <Checkbox
              key={m.id}
              id={`windx-modelo-${m.id}`}
              label={`${m.label}${sufixo}`}
              checked={idsSelecionados.includes(m.id)}
              disabled={indisponivel || semSuporteTipo}
              onChange={() => toggle(m.id)}
            />
          );
        })}

        {!isRadio && modelos.length > 0 && (
        <div className="d-flex gap-2 mt-2 mb-2">
          <Button
            variant="outline-primary"
            size="sm"
            className="flex-fill"
            onClick={() => onLimpar(modelos)}
            disabled={idsSelecionadosNoGrupo === 0}
            data-testid={`windx-modelos-nenhum-${chave}`}
          >
            Nenhum
          </Button>
          <Button
            variant="outline-primary"
            size="sm"
            className="flex-fill"
            onClick={() => onSelecionarTodos(modelos)}
            disabled={(
                  totalElegiveisGrupo === 0
                  || idsSelecionadosNoGrupo === totalElegiveisGrupo
                )}
            data-testid={`windx-modelos-todos-${chave}`}
          >
            Todos
          </Button>
        </div>
        )}
      </div>
    </div>
  );
}
